import { z } from "zod";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
import { ItemSplitSchema } from "./expenses.js";
/**
 * Receipt scanning: a client uploads a receipt image to S3 via a presigned
 * ticket, then asks the API to OCR (Textract) and structure (Bedrock/Gemma)
 * it into a best-effort expense draft. The draft prefills the normal
 * create-expense form — scanning never writes an expense directly. See
 * docs/receipt-scanning.md.
 */
/**
 * Content types accepted for a receipt upload. Images plus single-page PDF:
 * Textract's synchronous `DetectDocumentText` reads JPEG/PNG/PDF/TIFF from S3
 * directly, so a PDF receipt flows through the same OCR path as a photo.
 */
export const ReceiptContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/webp",
    "application/pdf",
]);
/** Body of `POST /scans/uploads`. */
export const CreateUploadTicketRequestSchema = z.strictObject({
    contentType: ReceiptContentTypeSchema,
    /** File size in bytes; capped server-side to reject oversized uploads. */
    byteSize: z.number().int().positive(),
});
/**
 * Response of `POST /scans/uploads`: a presigned PUT the client uploads the
 * image bytes to, plus the server-owned object key to pass back to the scan
 * endpoint. The client never chooses the key.
 */
export const UploadTicketSchema = z.strictObject({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/** Body of `POST /groups/{groupId}/scans`. */
export const CreateScanRequestSchema = z.strictObject({
    /** Object key returned by a prior upload ticket. Must be in the caller's prefix. */
    objectKey: z.string().min(1),
});
/**
 * A single drafted line item. Best-effort: only `label` is required so a
 * partially-recognised receipt still produces usable rows. `cost` is in minor
 * units (cents/pence) when present; `split` defaults to EQUAL client-side.
 */
export const DraftItemSchema = z.strictObject({
    label: z.string().min(1).max(200),
    cost: z.number().int().nonnegative().optional(),
    split: ItemSplitSchema.optional(),
});
/**
 * Per-field confidence (0..1) the model attached to drafted values, so the
 * client can highlight low-confidence cells for the user to check. All entries
 * optional — absence means "no signal".
 */
export const DraftConfidenceSchema = z.strictObject({
    label: z.number().min(0).max(1).optional(),
    currency: z.number().min(0).max(1).optional(),
    total: z.number().min(0).max(1).optional(),
});
/**
 * A translated copy of the drafted text, produced when the receipt is in a
 * language other than the group's target language (issue #284). Mirrors the
 * draft's `label` and item labels so the UI can show the original and the
 * translation side by side; money fields are not duplicated (numbers don't
 * translate). Absent when no translation was needed or possible.
 */
export const DraftTranslationSchema = z.strictObject({
    /** BCP-47 code the receipt was detected to be in (best-effort). */
    sourceLanguage: z.string().min(2).max(16).optional(),
    /** BCP-47 code the text was translated into (the group's language). */
    targetLanguage: z.string().min(2).max(16),
    /** Translated merchant/expense label, when the draft has one. */
    label: z.string().min(1).max(120).optional(),
    /** Translated item labels, index-aligned with `ExpenseDraft.items`. */
    items: z.array(z.string().min(1).max(200)).optional(),
});
/**
 * Best-effort expense draft. Every field is optional: the form renders with
 * whatever subset the pipeline produced and the user completes the rest. This
 * intentionally mirrors `CreateExpenseRequest` (label/currency/items) without
 * its `.min(1)` requirements.
 */
export const ExpenseDraftSchema = z.strictObject({
    label: z.string().min(1).max(120).optional(),
    currency: SupportedCurrencyCodesSchema.optional(),
    items: z.array(DraftItemSchema).optional(),
    /** Detected grand total in minor units, for reconciliation against items. */
    total: z.number().int().nonnegative().optional(),
    confidence: DraftConfidenceSchema.optional(),
    /**
     * Present when the receipt was translated into the group's language. The
     * original text stays in `label`/`items`; this carries the translated copy.
     */
    translation: DraftTranslationSchema.optional(),
});
/** Response of `POST /groups/{groupId}/scans`. */
export const ScanResultSchema = z.strictObject({
    draft: ExpenseDraftSchema,
    /** Credits charged for this scan (the reserved amount, after any refund). */
    creditsCharged: z.number().int().nonnegative(),
    remainingCredits: z.number().int().nonnegative(),
    /** SHA-256 of the raw OCR text, for support/debugging without storing PII. */
    rawTextHash: z.string().optional(),
});
